import type { AllowedRoles } from '@src/models/interfaces/access.interface';

const WRITE_ACTIONS = new Set(['CREATE', 'UPDATE', 'DELETE']);

/**
 * Every `ALLOWED_ROLES[...][CREATE|UPDATE|DELETE]` array, tracked by identity. The read-only
 * account gate (`useAllowedRoleChecker`) denies these lists while reads keep working, so every
 * existing `ComponentBlocker` / `ActionButton` / `allowedRole` on a mutation is covered without
 * touching its call site. A list shared with a GET/LIST key would block reads too: keep write
 * lists as their own literals.
 */
const WRITE_ROLE_LISTS = new WeakSet<readonly string[]>();

type RoleTree = { readonly [key: string]: RoleTree | readonly string[] };

export const registerWriteRoleLists = (
  tree: RoleTree,
  exclude: ReadonlyArray<readonly string[]> = [],
): void => {
  const excluded = new Set(exclude);
  Object.entries(tree).forEach(([key, node]) => {
    if (Array.isArray(node)) {
      if (WRITE_ACTIONS.has(key) && !excluded.has(node)) WRITE_ROLE_LISTS.add(node);
      return;
    }
    registerWriteRoleLists(node as RoleTree, exclude);
  });
};

export const isWriteRoleList = (roles: readonly string[] | null | undefined): boolean =>
  !!roles && WRITE_ROLE_LISTS.has(roles);

/**
 * Union of several `ALLOWED_ROLES` lists that keeps the write marker: the result is gated by
 * the read-only account mode when any input is a write list. Use it instead of spreading
 * (`[...X.DELETE, ...X.UPDATE]`), which silently drops the marker.
 */
export const mergeAllowedRoles = (...lists: ReadonlyArray<readonly string[]>): AllowedRoles => {
  const merged = Array.from(new Set(lists.flat())) as AllowedRoles;
  if (lists.some(isWriteRoleList)) WRITE_ROLE_LISTS.add(merged);
  return merged;
};
